import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';

import { ModelsService } from '@generated/services/models.service';
import { VoiceDto } from '@generated/models/voice-dto';

interface VoiceOption {
  label: string;        // shown in the dropdown row
  description: string;  // optional second line / search target
  searchKey: string;    // lowercased name + description for the filter
  value: string;        // voice id
  previewUrl: string | null;
}

/**
 * Voice picker bound to a specific TTS model version. Loads voices via
 * `apiModelsVersionsModelVersionIdVoicesGet` and renders a searchable
 * p-select where the filter matches both name and description.
 *
 * Inputs:
 *   - modelVersionId: TTS version to query voices for. When it changes,
 *     the list reloads. When empty, the picker is shown disabled.
 *   - selectedVoiceId: two-way bound voice id. Cleared automatically when
 *     the selected voice isn't present in the new list after a version
 *     change.
 */
@Component({
  selector: 'app-voice-picker',
  standalone: true,
  imports: [
    FormsModule,
    SelectModule,
    ProgressSpinnerModule,
    TooltipModule
],
  templateUrl: './voice-picker.component.html',
  styleUrl: './voice-picker.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VoicePickerComponent {
  private modelsService = inject(ModelsService);

  readonly modelVersionId = input<string | null | undefined>(null);
  readonly selectedVoiceId = input<string | null | undefined>(null);
  readonly disabled = input<boolean>(false);
  readonly selectedVoiceIdChange = output<string | null>();

  readonly voices = signal<VoiceDto[]>([]);
  readonly isLoading = signal<boolean>(false);
  readonly loadError = signal<boolean>(false);

  readonly options = computed<VoiceOption[]>(() =>
    this.voices().map(v => {
      const name = v.name ?? v.code ?? v.id ?? '';
      const description = (v.description ?? '').trim();
      return {
        label: name,
        description,
        searchKey: `${name} ${description}`.toLowerCase(),
        value: v.id ?? '',
        previewUrl: v.preview_url ?? null
      };
    })
  );

  readonly localSelected = signal<string | null>(null);

  constructor() {
    // Mirror the input into local state so the p-select can two-way bind
    // without us re-emitting on every external change.
    effect(() => {
      const incoming = this.selectedVoiceId() ?? null;
      if (this.localSelected() !== incoming) {
        this.localSelected.set(incoming);
      }
    });

    // Reload voices whenever the model version changes; clear the selected
    // voice if it isn't in the new list (otherwise the dropdown shows a
    // ghost id with no label).
    effect(() => {
      const versionId = this.modelVersionId();
      if (!versionId) {
        this.voices.set([]);
        this.loadError.set(false);
        return;
      }
      this.loadVoices(versionId);
    });
  }

  private loadVoices(versionId: string): void {
    this.isLoading.set(true);
    this.loadError.set(false);
    this.modelsService.apiModelsVersionsModelVersionIdVoicesGet({ modelVersionId: versionId })
      .subscribe({
        next: (result) => {
          const list = result.voices ?? [];
          this.voices.set(list);
          this.isLoading.set(false);
          const current = this.localSelected();
          if (current && list.some(v => v.id === current)) {
            // Уже выбранный голос всё ещё доступен — оставляем.
            return;
          }
          // Голос не выбран (или прежний исчез из списка) — авто-подставляем
          // первый доступный, чтобы у caller'а не оставался «нет голоса» при
          // валидной модели.
          const firstId = list[0]?.id ?? null;
          if (this.localSelected() !== firstId) {
            this.localSelected.set(firstId);
            this.selectedVoiceIdChange.emit(firstId);
          }
        },
        error: (error) => {
          console.error('Failed to load voices for version', versionId, error);
          this.voices.set([]);
          this.isLoading.set(false);
          this.loadError.set(true);
        }
      });
  }

  onSelectionChange(value: string | null): void {
    this.localSelected.set(value);
    this.selectedVoiceIdChange.emit(value);
  }

  /**
   * Filter callback bound to `p-select` — matches both the name and the
   * description so users can search by either ("warm female", "support",
   * a code like "alloy", etc.).
   */
  filterVoices(event: { filter: string }): boolean {
    // p-select's built-in filter operates per row when we pass a filter
    // function via `[filterMatchMode]="'custom'"` plus `(onFilter)`, but
    // we lean on a precomputed `searchKey` and PrimeNG's default
    // `contains` mode using `filterFields`. Returning true here keeps the
    // template-side wiring minimal.
    return !!event;
  }
}
